import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Google icon SVG component
const GoogleIcon = () => (
  <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
    <path
      fill="#4285F4"
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
    />
    <path
      fill="#34A853"
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
    />
    <path
      fill="#FBBC05"
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
    />
    <path
      fill="#EA4335"
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
    />
  </svg>
);

const Profile = ({ user, onLogout, onUserUpdate }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessedLink = useRef(false);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [linkingGoogle, setLinkingGoogle] = useState(false);
  const [isGoogleLinked, setIsGoogleLinked] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    role: '',
    address: '',
    profile_picture: '',
    driver_license: ''
  });
  const [previewProfilePic, setPreviewProfilePic] = useState(null);
  const [previewLicense, setPreviewLicense] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);
  
  // Handle Google link callback
  useEffect(() => {
    if (hasProcessedLink.current) return;
    
    const hash = window.location.hash;
    if (hash?.includes('session_id=') && hash.includes('link=true')) {
      hasProcessedLink.current = true;
      
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');
      
      if (sessionId) {
        handleGoogleLinkCallback(sessionId);
      }
    }
  }, [location]);

  const handleGoogleLinkCallback = async (sessionId) => {
    setLinkingGoogle(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/google/link`, {
        session_id: sessionId
      });
      
      toast.success(response.data.message);
      setIsGoogleLinked(true);
      
      // Clear the URL hash
      window.history.replaceState(null, '', window.location.pathname);
      
      // Refresh profile
      await fetchProfile();
      if (onUserUpdate) {
        await onUserUpdate();
      }
    } catch (error) {
      console.error('Error linking Google account:', error);
      toast.error(error.response?.data?.detail || 'Error al vincular cuenta de Google');
    } finally {
      setLinkingGoogle(false);
    }
  };

  const handleLinkGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    setLinkingGoogle(true);
    const redirectUrl = window.location.origin + '/profile#link=true';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/users/profile`);
      setProfileData({
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        role: response.data.role || '',
        address: response.data.address || '',
        profile_picture: response.data.profile_picture || '',
        driver_license: response.data.driver_license || ''
      });
      setPreviewProfilePic(response.data.profile_picture || null);
      setPreviewLicense(response.data.driver_license || null);
      
      // Check if Google is already linked
      setIsGoogleLinked(response.data.auth_provider === 'google' || response.data.google_linked === true);
    } catch (error) {
      console.error('Error fetching profile:', error);
      toast.error('Error al cargar el perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen debe ser menor a 2MB');
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten archivos de imagen');
      return;
    }

    try {
      // Convert to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        
        if (type === 'profile_picture') {
          setProfileData(prev => ({ ...prev, profile_picture: base64String }));
          setPreviewProfilePic(base64String);
        } else if (type === 'driver_license') {
          setProfileData(prev => ({ ...prev, driver_license: base64String }));
          setPreviewLicense(base64String);
        }
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Error al procesar la imagen');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const updateData = {
        name: profileData.name,
        phone: profileData.phone,
        address: profileData.address,
        profile_picture: profileData.profile_picture,
      };

      // Only include driver_license if user is a rider
      if (profileData.role === 'rider' && profileData.driver_license) {
        updateData.driver_license = profileData.driver_license;
      }

      await axios.put(`${BACKEND_URL}/api/users/profile`, updateData);
      toast.success('Perfil actualizado exitosamente');
      
      // Refresh profile data
      await fetchProfile();
      
      // Update user in global state to reflect changes in header avatar
      if (onUserUpdate) {
        await onUserUpdate();
      }
    } catch (error) {
      console.error('Error updating profile:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      'admin': 'Administrador',
      'dispensary_owner': 'Dueño de Dispensario',
      'rider': 'Rider',
      'support': 'IT/Soporte',
      'customer': 'Cliente'
    };
    return roleLabels[role] || role;
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-96">
          <div className="text-xl" style={{ color: '#0E2A24' }}>Cargando perfil...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold" style={{ color: '#0E2A24' }}>
            Mi Perfil
          </h1>
          <p className="text-gray-600 mt-2">
            Administra tu información personal y configuración de cuenta
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg overflow-hidden" style={{ border: '2px solid #D4AF37' }}>
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Left Column - Profile Picture */}
              <div className="flex flex-col items-center space-y-6">
                <div className="relative">
                  {previewProfilePic ? (
                    <img
                      src={previewProfilePic}
                      alt="Profile"
                      className="w-48 h-48 rounded-full object-cover border-4"
                      style={{ borderColor: '#D4AF37' }}
                    />
                  ) : (
                    <div 
                      className="w-48 h-48 rounded-full flex items-center justify-center text-6xl font-bold text-white border-4"
                      style={{ 
                        backgroundColor: '#0E2A24',
                        borderColor: '#D4AF37'
                      }}
                    >
                      {profileData.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>

                <div className="w-full">
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#0E2A24' }}>
                    Foto de Perfil
                  </label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageUpload(e, 'profile_picture')}
                    className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:text-white cursor-pointer"
                    style={{ 
                      fileButtonBackgroundColor: '#D4AF37',
                      fileButtonColor: '#0E2A24'
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-2">
                    Máximo 2MB. Formatos: JPG, PNG, WEBP
                  </p>
                </div>

                {/* Driver License (Only for Riders) */}
                {profileData.role === 'rider' && (
                  <div className="w-full pt-6 border-t" style={{ borderColor: '#E8E8E8' }}>
                    <label className="block text-sm font-semibold mb-3" style={{ color: '#0E2A24' }}>
                      Licencia de Conducir
                    </label>
                    
                    {previewLicense && (
                      <div className="mb-4">
                        <img
                          src={previewLicense}
                          alt="Driver License"
                          className="w-full h-auto rounded-lg border-2"
                          style={{ borderColor: '#D4AF37' }}
                        />
                      </div>
                    )}
                    
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageUpload(e, 'driver_license')}
                      className="w-full text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:text-white cursor-pointer"
                      style={{ 
                        fileButtonBackgroundColor: '#D4AF37',
                        fileButtonColor: '#0E2A24'
                      }}
                    />
                    <p className="text-xs text-gray-500 mt-2">
                      Sube una foto clara de tu licencia de conducir
                    </p>
                  </div>
                )}
              </div>

              {/* Right Column - Form Fields */}
              <div className="space-y-6">
                {/* Name */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#0E2A24' }}>
                    Nombre Completo
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={profileData.name}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:border-opacity-100 transition"
                    style={{ 
                      borderColor: '#E8E8E8',
                      focusBorderColor: '#D4AF37'
                    }}
                    placeholder="Tu nombre completo"
                  />
                </div>

                {/* Email (Read-only) */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#0E2A24' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={profileData.email}
                    disabled
                    className="w-full px-4 py-3 border-2 rounded-lg bg-gray-100 cursor-not-allowed"
                    style={{ borderColor: '#E8E8E8' }}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    El email no puede ser modificado
                  </p>
                </div>

                {/* Role (Read-only) */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#0E2A24' }}>
                    Rol
                  </label>
                  <input
                    type="text"
                    value={getRoleLabel(profileData.role)}
                    disabled
                    className="w-full px-4 py-3 border-2 rounded-lg bg-gray-100 cursor-not-allowed"
                    style={{ borderColor: '#E8E8E8' }}
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#0E2A24' }}>
                    Teléfono
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:border-opacity-100 transition"
                    style={{ 
                      borderColor: '#E8E8E8',
                      focusBorderColor: '#D4AF37'
                    }}
                    placeholder="+1 234 567 8900"
                  />
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: '#0E2A24' }}>
                    Dirección
                  </label>
                  <textarea
                    name="address"
                    value={profileData.address}
                    onChange={handleInputChange}
                    rows="3"
                    className="w-full px-4 py-3 border-2 rounded-lg focus:outline-none focus:border-opacity-100 transition resize-none"
                    style={{ 
                      borderColor: '#E8E8E8',
                      focusBorderColor: '#D4AF37'
                    }}
                    placeholder="Calle, Ciudad, Estado, Código Postal"
                  />
                </div>
              </div>
            </div>

            {/* Save Button */}
            <div className="mt-8 flex justify-end">
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-8 py-3 rounded-lg font-semibold text-lg transition-all duration-300 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{
                  backgroundColor: '#D4AF37',
                  color: '#0E2A24'
                }}
              >
                {saving ? 'Guardando...' : 'GUARDAR'}
              </button>
            </div>
          </div>
        </div>
        
        {/* Google Account Link Section */}
        <div className="mt-8 bg-white rounded-xl shadow-lg overflow-hidden" style={{ border: '2px solid #E8E8E8' }}>
          <div className="p-6">
            <h2 className="text-xl font-bold mb-4" style={{ color: '#0E2A24' }}>
              Vinculación de Cuenta
            </h2>
            <p className="text-gray-600 mb-4">
              Vincula tu cuenta con Google para iniciar sesión más rápido.
            </p>
            
            {isGoogleLinked ? (
              <div className="flex items-center p-4 bg-green-50 rounded-lg border border-green-200">
                <svg className="w-6 h-6 text-green-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                </svg>
                <div>
                  <p className="font-semibold text-green-700">Cuenta vinculada con Google</p>
                  <p className="text-sm text-green-600">Puedes iniciar sesión con tu contraseña o con Google.</p>
                </div>
              </div>
            ) : (
              <button
                onClick={handleLinkGoogle}
                disabled={linkingGoogle}
                className="flex items-center justify-center w-full md:w-auto px-6 py-3 border-2 rounded-lg font-medium transition-all duration-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ borderColor: '#E8E8E8' }}
                data-testid="link-google-button"
              >
                <GoogleIcon />
                {linkingGoogle ? 'Vinculando...' : 'Vincular con Google'}
              </button>
            )}
            
            <p className="text-xs text-gray-500 mt-3">
              Al vincular tu cuenta, podrás iniciar sesión con tu email/contraseña o con Google.
              Tu contraseña actual seguirá funcionando.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Profile;
